import { STEPPER_GREEN, DODGER_BLUE } from 'constants/colors'

export const SLOT_LIST_LIMIT = 22

/**
 * Default slot-availability **calendar** window: **21 consecutive days**
 * (anchor day plus 20 more). Same as `addDays(anchor, SLOT_CALENDAR_LAST_DAY_OFFSET_FROM_ANCHOR)`.
 * Use for max-range UI (e.g. paginator “next” cap) and any DOW/TOD lookahead scan
 * that must stay aligned with loaded slot data (not `SLOT_LIST_LIMIT`, which can differ).
 */
export const SLOT_CALENDAR_SPAN_DAYS = 21

/** `SLOT_CALENDAR_SPAN_DAYS - 1` — inclusive end offset from anchor (20). */
export const SLOT_CALENDAR_LAST_DAY_OFFSET_FROM_ANCHOR =
  SLOT_CALENDAR_SPAN_DAYS - 1

/**
 * Extended-booking horizon (virtual slots). Configurable via env for now;
 * will be promoted to a hard constant later. Defaults to 10 weeks.
 */
export const EXTENDED_BOOKING_VIEW_ALL_WEEKS =
  Number(process.env.NEXT_PUBLIC_EXTENDED_BOOKING_VIEW_ALL_WEEKS) || 10
export const EXTENDED_BOOKING_SPAN_DAYS = EXTENDED_BOOKING_VIEW_ALL_WEEKS * 7
/** Prefix for synthetic virtual-slot ids (no pre-generated `slots` row). */
export const VIRTUAL_SLOT_ID_PREFIX = 'virtual-'
/** User-facing toast when GET /virtual-slots fails. */
export const VIRTUAL_SLOTS_LOAD_ERROR =
  'Failed to load some availability. Please try again.'
/** Inclusive end offset from anchor for extended booking. */
export const EXTENDED_BOOKING_LAST_DAY_OFFSET_FROM_ANCHOR =
  EXTENDED_BOOKING_SPAN_DAYS - 1

// Low availability thresholds
export const LOW_AVAILABILITY_MAX_PROVIDERS = 2
export const LOW_AVAILABILITY_WITH_FEW_SLOTS_MAX_PROVIDERS = 5
export const LOW_AVAILABILITY_MAX_SLOTS_PER_PROVIDER = 1

export const FILTER_SEARCH_PLACEHOLDER = {
  language: 'Provider languages',
  counseling_approach: 'Therapy approaches',
}

export const OTHER_GENDER_VALUE = 'Other/choose not to respond'

export const NON_BINARY_GENDER_VALUE = 'Non-binary/non-conforming'

export const NON_BINARY_GENDER_DISPLAY_VALUE =
  'Non-binary / gender non-conforming'

export const REQUIRED_GENDER_VALUE_ORDER = [
  'Female',
  'Male',
  NON_BINARY_GENDER_VALUE,
  'Transgender',
]

export const RETURN_FILTER_KEY_MAPPING = {
  availability_type: 'availabilityType',
  area_of_concern: 'areaOfConcern',
  language: 'language',
  counseling_approach: 'counselingApproach',
  client_age_group: 'clientAgeGroup',
  gender: 'gender',
  time_of_day: 'timeOfDay',
  day_of_week: 'dayOfWeek',
  insurance_id: 'insuranceId',
} as const

export const FILTER_KEY_MAPPING = {
  availabilityType: 'availability_type',
  areaOfConcern: 'area_of_concern',
  language: 'language',
  counselingApproach: 'counseling_approach',
  clientAgeGroup: 'client_age_group',
  gender: 'gender',
  counselingType: 'counseling_type',
  timeOfDay: 'time_of_day',
  dayOfWeek: 'day_of_week',
  insuranceId: 'insurance_id',
}

export const LabelAppointmentMapping = {
  'In Person': 'in_person',
  Online: 'online',
}

export const AvailabilityFilterValues = {
  online: 'Online',
  inPerson: 'In Person',
}

export const availabilityColorMapping = {
  online: DODGER_BLUE,
  in_person: STEPPER_GREEN,
}
export const availabilityIconMapping = {
  online: '/icons/icon-camera.svg',
  in_person: '/icons/icon-in-person.svg',
}

export enum SortFilterKeys {
  DISTANCE = 'distance',
  AVAILABILITY = 'most_available',
}

export enum ShowSearchCondition {
  NEVER,
  ALWAYS,
  ON_EXPANDED_LIST,
  ON_LARGE_LIST,
}

export const sortFilterOptions: SelectItem[] = [
  { label: 'Sort by Distance', value: SortFilterKeys.DISTANCE },
  { label: 'Sort by Most Available', value: SortFilterKeys.AVAILABILITY },
]

export const orderByFilters: ProviderFilter[] = [
  {
    description: null,
    id: 'distance',
    mappingValue: null,
    priority: 2,
    type: 'orderBy', // API also accepts this value.
    value: 'Sort by Distance',
  },
  {
    description: null,
    id: 'most_available',
    mappingValue: null,
    priority: 2,
    type: 'orderBy', // API also accepts this value.
    value: 'Sort by Most Available',
  },
]

export enum ProviderDetailsAPIErrors {
  PROVIDER_DELETED = 'Provider Deleted',
  INCORRECT_PRACTITIONER = 'Incorrect Practitioner Type',
}

export const SLOT_LOCATION_MAP = {
  direct_match: 'direct_search_match',
  indirect_match: 'indirect_search_match',
  providers_profile: 'providers_profile',
  availability_modal: 'all_availability_shown_modal',
  next_opening: 'next_opening',
  similar_providers_next_opening: 'similar_providers_next_opening',
  se_next_opening: 'se_next_opening',
}

/** Query key that carries a search-card match type onto the provider profile. */
export const PROVIDER_PROFILE_SLOT_LOCATION_QUERY = 'slotLocation'

export type SearchMatchSlotLocation =
  | typeof SLOT_LOCATION_MAP.direct_match
  | typeof SLOT_LOCATION_MAP.indirect_match

/**
 * Match type for a provider opened from search results.
 * Direct and indirect are the only values that belong on
 * Providers Profile Page Viewed. Other slot locations (profile, modal,
 * next opening) are omitted.
 */
export const getSearchMatchSlotLocation = (
  value: unknown
): SearchMatchSlotLocation | undefined => {
  const raw = Array.isArray(value) ? value[0] : value

  if (
    raw === SLOT_LOCATION_MAP.direct_match ||
    raw === SLOT_LOCATION_MAP.indirect_match
  ) {
    return raw
  }

  return undefined
}

export const PRIMARY_FILTERS = [
  'location',
  'counselingType',
  'clientAgeGroup',
  'dayOfWeek',
]

// the filters are listed on increasing order of priority. So counselingApproach is lowest priority
// (meaning it's removed first while forming waterfall buckets.)
export const SECONDARY_FILTERS = [
  'counselingApproach',
  'language',
  'availabilityType',
  'gender',
  'timeOfDay',
  'areaOfConcern',
  'insuranceId',
]

/**
 * Secondary filters the waterfall may relax for the current search.
 * Dropping `insuranceId` is what turns the waterfall into a self-pay offer, so a
 * Medicaid payer keeps it applied and is never funnelled into self-pay.
 */
export const getDroppableSecondaryFilters = (
  insuranceProvider?: Partial<InsuranceProvider> | null
): string[] =>
  insuranceProvider?.isMedicaid
    ? SECONDARY_FILTERS.filter((filter) => filter !== 'insuranceId')
    : SECONDARY_FILTERS

export const SECONDARY_OR_MULTISELECT_FILTERS = ['timeOfDay']

/**
 * The waterfall mounts when a search returns fewer exact matches than this. The
 * simplification test does not move it: the component still has to render at 6-9
 * exact matches so it can host the "show more" control, even though it auto-fills
 * nothing there.
 */
export const WATERFALL_MOUNT_MAX_DIRECT_MATCHES = 10

/**
 * THRIV-13273 variant: the initial view never exceeds this many providers in total,
 * so the partial-match budget is this minus the exact-match count (5 exact leaves
 * room for 1 partial, 0 exact for 6). At 6+ exact matches the budget is 0.
 */
export const WATERFALL_VARIANT_MAX_INITIAL_RESULTS = 6

/** THRIV-13273 variant: how many additional providers each "show more" reveals. */
export const WATERFALL_VARIANT_REVEAL_STEP = 4

// Defined the call center schedule in total minutes for each day
export const callCenterSchedule = [
  { open: 8 * 60, close: 18 * 60 }, // Sunday: 8 AM - 6 PM
  { open: 8 * 60, close: 23 * 60 }, // Monday: 8 AM - 11:00 PM Eastern
  { open: 8 * 60, close: 23 * 60 }, // Tuesday: 8 AM - 11:00 PM Eastern
  { open: 8 * 60, close: 23 * 60 }, // Wednesday: 8 AM - 11:00 PM Eastern
  { open: 8 * 60, close: 23 * 60 }, // Thursday: 8 AM - 11:00 PM Eastern
  { open: 8 * 60, close: 23 * 60 }, // Friday: 8 AM - 11:00 PM Eastern
  { open: 8 * 60, close: 18 * 60 }, // Saturday: 8 AM - 6 PM
]

export const selfPayCheckListItem = {
  id: 'self-pay',
  name: 'Self-Pay / Out-of-pocket',
}

export const outOfPocketCheckListItem = {
  id: 'self-pay',
  name: 'Out-of-pocket',
}

// TODO: remember to update this once the url is defined by integrev/WP team
export const wpFormPath = '/contact-form'
const isProduction = process.env.NEXT_PUBLIC_VERCEL_ENV === 'production'
export const wpFormLandingPageUrl = `${
  isProduction ? 'https://thriveworks.com/' : 'https://dev.thriveworks.com'
}${wpFormPath}/`

export const CARD_LIST_VISIBLE_COUNT = 4
export const CARD_LIST_MORE_TEXT = '+ more'
